import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Search, LayoutDashboard, Calculator, BookOpen, Package, Repeat, Scale, History as HistoryIcon, MessagesSquare, BookOpenCheck, User, Settings, Utensils } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";

import { useSettings } from "../context/SettingsContext";

/**
 * Global ⌘K / Ctrl+K command palette. Fuzzy-matches peptide names, saved
 * vials, and static app pages. Uses shadcn's Dialog primitive for the frame
 * so it inherits the current Theme Studio tokens automatically.
 */
const PAGES = [
  { title: "Dashboard", icon: LayoutDashboard, route: "/dashboard" },
  { title: "Peptide Calculator", icon: Calculator, route: "/calculators", flag: "calculators" },
  { title: "Nutrition Calculator", icon: Utensils, route: "/calculators?tab=nutrition", flag: "calculators" },
  { title: "Library", icon: BookOpen, route: "/library", flag: "library" },
  { title: "Inventory", icon: Package, route: "/inventory", flag: "inventory" },
  { title: "Cycles", icon: Repeat, route: "/cycles", flag: "cycles" },
  { title: "Weight", icon: Scale, route: "/weight", flag: "weight" },
  { title: "Nutrition", icon: Utensils, route: "/nutrition", flag: "nutrition" },
  { title: "History", icon: HistoryIcon, route: "/history", flag: "history" },
  { title: "Community", icon: MessagesSquare, route: "/community", flag: "community" },
  { title: "Playbook", icon: BookOpenCheck, route: "/playbook", flag: "playbook" },
  { title: "Profile", icon: User, route: "/profile" },
];
const ADMIN_PAGES = [
  { title: "Site Settings", icon: Settings, route: "/admin/settings" },
  { title: "Theme Studio", icon: Settings, route: "/admin/theme" },
];
const STAFF_PAGES = [
  { title: "Moderation · Users", icon: User, route: "/admin/users" },
];

function fuzzy(hay, needle) {
  if (!needle) return true;
  const h = String(hay || "").toLowerCase();
  const n = needle.toLowerCase();
  if (h.includes(n)) return true;
  // sub-sequence match
  let i = 0;
  for (const c of h) { if (c === n[i]) i++; if (i === n.length) return true; }
  return false;
}

export default function CommandPalette({ isAdmin, isStaff }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [peptides, setPeptides] = useState([]);
  const [vials, setVials] = useState([]);
  const navigate = useNavigate();
  const { settings } = useSettings();
  const flags = settings?.feature_flags || {};

  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  useEffect(() => {
    if (!open) return;
    setQ("");
    (async () => {
      try {
        const [pRes, vRes] = await Promise.all([
          api.get("/peptides").catch(() => ({ data: [] })),
          api.get("/vials").catch(() => ({ data: [] })),
        ]);
        setPeptides(pRes.data || []);
        setVials(vRes.data || []);
      } catch {}
    })();
  }, [open]);

  const pageResults = useMemo(() => {
    const list = [
      ...PAGES,
      ...((isStaff || isAdmin) ? STAFF_PAGES : []),
      ...(isAdmin ? ADMIN_PAGES : []),
    ].filter((p) => !p.flag || flags[p.flag] !== false);
    return list.filter((p) => fuzzy(p.title, q)).slice(0, 8);
  }, [q, isAdmin, isStaff, flags]);

  const peptideResults = useMemo(
    () => peptides.filter((p) => fuzzy(p.name, q) || fuzzy(p.category, q)).slice(0, 6),
    [peptides, q]
  );

  const vialResults = useMemo(
    () => vials.filter((v) => fuzzy(v.peptide_name || "", q)).slice(0, 6),
    [vials, q]
  );

  const go = (route) => { setOpen(false); navigate(route); };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-xl p-0 bg-[color:var(--surface)] border border-[color:var(--border)] rounded-none" data-testid="command-palette">
        <DialogHeader className="sr-only">
          <DialogTitle>Command palette</DialogTitle>
          <DialogDescription>Jump to any page, peptide, or vial.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 border-b border-[color:var(--border)] px-3">
          <Search className="w-4 h-4 text-[color:var(--text-muted)]" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search pages, peptides, vials…"
            className="flex-1 bg-transparent border-0 outline-none py-3 text-sm text-[color:var(--text)] placeholder:text-[color:var(--text-muted)]"
            data-testid="command-palette-input"
          />
          <kbd className="font-mono-num text-[10px] uppercase text-[color:var(--text-muted)] border border-[color:var(--border)] px-1.5 py-0.5">ESC</kbd>
        </div>
        <div className="max-h-[420px] overflow-y-auto py-2">
          {pageResults.length > 0 && <Section title="Pages">
            {pageResults.map((p) => (
              <Row key={p.route} icon={p.icon} label={p.title} onClick={() => go(p.route)} testid={`palette-page-${p.title.replace(/\s+/g, "-").toLowerCase()}`} />
            ))}
          </Section>}
          {peptideResults.length > 0 && <Section title="Peptides">
            {peptideResults.map((p) => (
              <Row key={p.id} icon={BookOpen} label={p.name} sub={p.category} onClick={() => go(`/library?peptide=${p.id}`)} testid={`palette-peptide-${p.id}`} />
            ))}
          </Section>}
          {vialResults.length > 0 && <Section title="Vials">
            {vialResults.map((v) => (
              <Row key={v.id} icon={Package} label={v.peptide_name || "Unnamed vial"} sub={`${v.strength_mg || "?"} mg`} onClick={() => go(`/inventory`)} testid={`palette-vial-${v.id}`} />
            ))}
          </Section>}
          {pageResults.length + peptideResults.length + vialResults.length === 0 && (
            <div className="text-center py-10 text-[color:var(--text-muted)] text-sm font-mono-num uppercase tracking-widest">No results</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-2">
      <div className="px-3 py-1 font-mono-num text-[10px] uppercase tracking-[0.3em] text-[color:var(--text-muted)]">// {title}</div>
      <div>{children}</div>
    </div>
  );
}
function Row({ icon: Icon, label, sub, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-[color:var(--surface-2)] text-left transition-colors"
      data-testid={testid}
    >
      <Icon className="w-4 h-4 text-[color:var(--brand)] shrink-0" />
      <span className="text-sm text-[color:var(--text)] flex-1 truncate">{label}</span>
      {sub && <span className="font-mono-num text-[10px] uppercase text-[color:var(--text-muted)] tracking-widest">{sub}</span>}
    </button>
  );
}
